import * as React from "react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@/ui/components/ui/sidebar"

import { NavMain } from "@/ui/components/nav-main"
import { NavSecondary } from "@/ui/components/nav-secondary"
import { NavUser } from "@/ui/components/nav-user"
import { useLocation } from "react-router-dom";
import { Organization } from "@/machines/types/organization.ts";
import { TeamSwitcher } from "@/ui/components/team-switcher.tsx";
import { Logo } from "@/shared/components/common/logo";

type NavItem = {
  title: string
  url: string
  icon?: React.ElementType
  isActive?: boolean
  badge?: React.ElementType
  items?: { title: string; url: string }[]
}

type NavGroup = {
  label: string
  items: NavItem[]
}

type NavUserType = {
  name: string
  email: string
  avatar: string
}

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  navMain: NavGroup[]
  navSecondary?: NavItem[]
  user?: NavUserType
  organizations: Organization[];
  sidebarFooterContent?: React.ReactNode;
}

export function AppSidebar({
                             navMain,
                             navSecondary = [],
                             user,
                             organizations,
                             sidebarFooterContent,
                             ...props
                           }: AppSidebarProps) {
  const { pathname } = useLocation()
  const groupsWithActive = React.useMemo(() => {
    return navMain.map((group) => ({
      label: group.label,
      items: group.items.map((item) => ({
        ...item,
        // Compare against the path only — the Export item carries a ?query.
        isActive:
          pathname === item.url.split("?")[0] ||
          item.items?.some((sub) => pathname === sub.url.split("?")[0]),
      })),
    }))
  }, [pathname, navMain])

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="border-b border-sidebar-border pb-2 mb-1">
            {/* Brand safe zone — clear space around the mark so nothing crowds
                it. Full wordmark on desktop when expanded; favicon-only on
                mobile and when the rail is collapsed. */}
            <div className="px-2 py-1.5 group-data-[collapsible=icon]:md:px-0">
              <Logo
                alt="MineMaestro"
                className="h-6 w-auto hidden md:block group-data-[collapsible=icon]:md:hidden"
              />
              <img
                src="/logos/favicon/favicon.png"
                alt="MineMaestro"
                className="h-6 w-6 md:hidden group-data-[collapsible=icon]:md:block group-data-[collapsible=icon]:md:mx-auto"
              />
            </div>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <TeamSwitcher organizations={organizations} />

          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {groupsWithActive.map((group) => (
          <NavMain key={group.label} label={group.label} items={group.items} />
        ))}
        {navSecondary.length > 0 && (
          <NavSecondary items={navSecondary} className="mt-auto" />
        )}
      </SidebarContent>
      <SidebarFooter>
        {sidebarFooterContent}
        {user ? <NavUser user={user} /> : null}
      </SidebarFooter>
    </Sidebar>
  )
}
